'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useKlaviyo } from '@/hooks/useKlaviyo';
import { trackViewContent } from '@/lib/tracking/events';
import { competitionService } from '@/lib/services/competitionService';
import { pastWinnersService } from '@/lib/services/pastWinnersService';
import CompetitionHero from './CompetitionHero';
import PrizeGallery from './PrizeGallery';
import CompetitionInfo from './CompetitionInfo';
import FreeEntryNotice from './FreeEntryNotice';
import LiveDrawEmbed from '@/components/common/LiveDrawEmbed';
import ManualNumberSelector from './ManualNumberSelector';
import TicketSelector from './TicketSelector';
import LuckyNumbers from './LuckyNumbers';
import CompetitionRules from './CompetitionRules';
import EntrantsList from './EntrantsList';

import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import { useOpened } from '@/lib/useOpened';
import { formatDraw } from '@/lib/homepage/format';
import { subscribeToGrid } from '@/lib/realtime/liveCompetitions';
import { splitGridMessage, applyToHolds, applyToSold, applyMyHoldTimes } from '@/lib/realtime/gridEvents';

const CompetitionDetailInteractive = ({ initialData }) => {
  const { user } = useAuth();
  const { track } = useKlaviyo();
  const [competition, setCompetition] = useState(initialData?.competition || null);
  const [pastWinners, setPastWinners] = useState(initialData?.pastWinners || []);
  // Published ahead of time: opens for entries at its start date (a hook,
  // so it stays above the early returns below).
  const opened = useOpened(competition?.startDate);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [userSelectedNumbers, setUserSelectedNumbers] = useState([]);
  const [reservedNumbers, setReservedNumbers] = useState([]);
  const [userReservedNumbers, setUserReservedNumbers] = useState([]);
  // NEW: Add state for tracking purchased tickets by current user and others
  const [userPurchasedNumbers, setUserPurchasedNumbers] = useState([]);
  const [othersPurchasedNumbers, setOthersPurchasedNumbers] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(!initialData?.competition);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [showNumberSelector, setShowNumberSelector] = useState(false);
  const [instantWinPrizes, setInstantWinPrizes] = useState([]); // NEW: Add state for instant win prizes

  // NEW: Add expiration timer state (kept to preserve existing behaviour, even if unused)
  const [expirationTimers, setExpirationTimers] = useState(new Map());

  const [isReservationMinimised, setIsReservationMinimised] = useState(false);

  // MISSING IN YOUR PASTE: this is used later
  const [isProcessing, setIsProcessing] = useState(false);

  // Track Viewed Competition once per page load (fires when competition data is ready)
  const trackedCompetitionRef = useRef(null);
  useEffect(() => {
    if (!competition?.id || trackedCompetitionRef.current === competition.id) return;
    trackedCompetitionRef.current = competition.id;
    track('Viewed Competition', {
      competition_id: competition.id,
      title: competition.title,
      slug: competition.slug ?? null,
      ticket_price: competition.ticket_price ?? null,
      prize_value: competition.prize_value ?? null,
      category: competition.category ?? null,
      competition_type: competition.instant_win_type ? 'instant_win' : 'prize_draw',
    });
    trackViewContent(competition);
  }, [competition?.id]);
  const refreshIntervalRef = useRef(null);

  // NEW: Ref to track expiration interval
  const expirationIntervalRef = useRef(null);

  // --------------------------------------------
  // A number changed (bought or released): reload the grid, at most about
  // once a second. Other players' purchases and holds can't be read row by
  // row (no player ids leave the database), so the grid function is the
  // one source of truth.
  // --------------------------------------------
  const loadNumberStatesRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const refreshSingleTicketState = useCallback(compId => {
    if (!compId || refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadNumberStatesRef.current?.(compId);
    }, 800);
  }, []);

  // The player's own holds and purchases, and other players' holds, for
  // live handlers and timers that run outside render.
  const userReservedRef = useRef([]);
  useEffect(() => {
    userReservedRef.current = userReservedNumbers || [];
  }, [userReservedNumbers]);
  const userPurchasedRef = useRef([]);
  useEffect(() => {
    userPurchasedRef.current = userPurchasedNumbers || [];
  }, [userPurchasedNumbers]);
  const reservedRef = useRef([]);
  useEffect(() => {
    reservedRef.current = reservedNumbers || [];
  }, [reservedNumbers]);

  // How far the screen's clock is behind the database's (ms). Hold end
  // times are the database's, so a fast or slow clock mustn't end them early
  // or late. Measured on every grid load.
  const clockOffsetRef = useRef(0);
  // Holds the player has already been told have run out (number@end).
  const toldEndedRef = useRef(new Set());

  // Numbers this player has just asked to hold: their own live message can
  // arrive before the reply, and mustn't show them as someone else's.
  const inFlightRef = useRef(new Map()); // number -> when asked
  const markInFlight = numbers => {
    const at = Date.now();
    (numbers || []).forEach(n => inFlightRef.current.set(n, at));
    return () =>
      setTimeout(() => {
        (numbers || []).forEach(n => {
          if (inFlightRef.current.get(n) === at) inFlightRef.current.delete(n);
        });
      }, 3000);
  };

  // Add or update holds by number (a second click on the same number
  // returns the same hold).
  const mergeHolds = (prev, next) => {
    const byNumber = new Map((prev || []).map(r => [r?.number, r]));
    (next || []).forEach(r => byNumber.set(r?.number, { ...(byNumber.get(r?.number) || {}), ...r }));
    return Array.from(byNumber.values());
  };

  // Load competition data if not provided via initialData
  useEffect(() => {
    if (!initialData?.competition) {
      const loadCompetitionData = async () => {
        try {
          setLoading(true);
          setError('');

          // Load first available competition if no specific competition provided
          const competitions = await competitionService?.getAll();
          if (competitions?.length > 0) {
            const comp = competitions?.[0];
            setCompetition(comp);

            // Guests see sold and held numbers too.
            if (comp?.id) {
              await loadNumberStates(comp?.id);
            }

            // NEW: Load instant win prizes if this is an instant win competition
            if (comp?.instant_win_type === 'instant_win') {
              const prizes = await competitionService?.getInstantWinPrizes(comp?.id);
              setInstantWinPrizes(prizes || []);
            }

            // Load past winners
            const winners = await pastWinnersService?.getAll();
            setPastWinners(winners || []);
          }
        } catch (err) {
          setError(err?.message || 'Failed to load competition data');
          console.error('Competition load error:', err);
        } finally {
          setLoading(false);
        }
      };

      loadCompetitionData();
    } else {
      // Guests see sold and held numbers too; signing in reloads with the
      // player's own numbers.
      if (competition?.id) {
        loadNumberStates(competition?.id);
      }

      // NEW: Load instant win prizes if this is an instant win competition
      if (competition?.id && competition?.instant_win_type === 'instant_win') {
        competitionService
          ?.getInstantWinPrizes(competition?.id)
          ?.then(prizes => setInstantWinPrizes(prizes || []))
          ?.catch(err => console.error('Error loading instant win prizes:', err));
      }
    }
  }, [initialData, competition?.id, user?.id]);

  // Enhanced refresh function for initial snapshot only (kept)
  const loadNumberStates = useCallback(
    async compId => {
      if (!compId) return;

      try {
        // One call for guests and players: every sold number, numbers other
        // players are holding (no player ids), and the signed-in player's
        // own purchases, picks and basket tickets in this competition.
        const askedAt = Date.now();
        const numberStates = await competitionService?.getNumberStates(compId);
        const serverNow = numberStates?.serverTime ? new Date(numberStates.serverTime).getTime() : NaN;
        if (Number.isFinite(serverNow)) {
          clockOffsetRef.current = serverNow - (askedAt + Date.now()) / 2;
        }

        setSelectedNumbers(numberStates?.selected || []);
        setReservedNumbers(numberStates?.reserved || []);
        setUserPurchasedNumbers(numberStates?.userPurchased || []);
        setOthersPurchasedNumbers(numberStates?.othersPurchased || []);
        // Every row in selected_numbers is already bought; picks and basket
        // tickets are holds.
        setUserSelectedNumbers([]);
        setUserReservedNumbers(numberStates?.userReserved || []);
        setLastRefresh(Date.now());
      } catch (err) {
        console.error('Failed to load number states:', err);
      }
    },
    []
  );
  loadNumberStatesRef.current = loadNumberStates;

  // ------------------------------------------------------------
  // Live grid. The database announces every change to this competition's
  // numbers (picks, deselects, lucky dips, baskets, holds running out,
  // purchases) on comp:<id>, without player ids, so guests listen too.
  // Changes to the player's own numbers reload the grid instead: only the
  // grid function can say which numbers are theirs.
  // ------------------------------------------------------------
  useEffect(() => {
    if (!competition?.id) return undefined;
    const compId = competition.id;

    const onGrid = payload => {
      const now = Date.now();
      inFlightRef.current.forEach((since, n) => {
        if (now - since > 15000) inFlightRef.current.delete(n);
      });
      const { others, mineHeld, mineChanged } = splitGridMessage(payload, {
        holds: new Set((userReservedRef.current || []).map(r => r?.number)),
        purchased: new Set(userPurchasedRef.current || []),
        inFlight: new Set(inFlightRef.current.keys())
      });
      if (others.sold.length || others.held.length || others.free.length) {
        setReservedNumbers(prev => applyToHolds(prev, others));
        setOthersPurchasedNumbers(prev => applyToSold(prev, others));
        setSelectedNumbers(prev => applyToSold(prev, others));
      }
      if (mineHeld.length) {
        setUserReservedNumbers(prev => applyMyHoldTimes(prev, mineHeld));
      }
      if (mineChanged) refreshSingleTicketState(compId);
    };

    const unsubscribe = subscribeToGrid(compId, onGrid, status => {
      // Joined or rejoined: catch up on anything sent while away.
      if (status === 'SUBSCRIBED') refreshSingleTicketState(compId);
    });

    // Lucky-dip tickets added from this page are the player's own: reload so
    // they show as theirs, not as someone else's holds.
    const onCartChanged = () => refreshSingleTicketState(compId);
    window.addEventListener('cart:changed', onCartChanged);

    return () => {
      unsubscribe();
      window.removeEventListener('cart:changed', onCartChanged);
    };
  }, [competition?.id, refreshSingleTicketState]);

  // Holds end on the second: each screen drops a hold when its time is up
  // (the database also announces it when its clean-up runs). Runs for
  // guests too.
  useEffect(() => {
    if (!competition?.id) return undefined;
    const compId = competition.id;

    const ended = (r, now) => Boolean(r?.expiresAt) && new Date(r.expiresAt).getTime() <= now;
    const checkExpirations = () => {
      // The database's time, not the screen's.
      const now = Date.now() + clockOffsetRef.current;
      if ((reservedRef.current || []).some(r => ended(r, now))) {
        setReservedNumbers(prev => (prev || []).filter(r => !ended(r, now)));
      }
      const mineEnded = (userReservedRef.current || []).filter(r => ended(r, now));
      if (mineEnded.length > 0) {
        setUserReservedNumbers(prev => (prev || []).filter(r => !ended(r, now)));
        const untold = mineEnded.filter(r => !toldEndedRef.current.has(`${r?.number}@${r?.expiresAt}`));
        if (untold.length > 0) {
          untold.forEach(r => toldEndedRef.current.add(`${r?.number}@${r?.expiresAt}`));
          setError(
            `⏰ Your hold on ${untold.map(r => `#${r?.number}`).join(', ')} has run out. Pick again if it's still free.`
          );
          setTimeout(() => setError(''), 5000);
          refreshSingleTicketState(compId);
        }
      }
    };
    expirationIntervalRef.current = setInterval(checkExpirations, 1000);

    // Backstop: reload the whole grid every 30 seconds while the tab is
    // visible, and straight away when it becomes visible again (a
    // background tab can miss messages).
    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = setInterval(() => {
        if (competition?.id) loadNumberStates(competition.id);
      }, 30000);
    };
    const stopPolling = () => {
      if (pollId) { clearInterval(pollId); pollId = null; }
    };
    const onVisible = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        if (competition?.id) loadNumberStates(competition.id);
        startPolling();
      } else {
        stopPolling();
      }
    };
    startPolling();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      if (expirationIntervalRef?.current) {
        clearInterval(expirationIntervalRef?.current);
        expirationIntervalRef.current = null;
      }
      stopPolling();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [competition?.id, loadNumberStates, refreshSingleTicketState]);

  // Add event listener for select numbers button
  useEffect(() => {
    const handleSelectNumbers = () => {
      if (competition?.instant_win_type === 'instant_win' || competition?.instantWinType === 'instant_win') {
        return;
      }

      setActiveTab('numbers');
    };

    // Make setActiveTab available globally for TicketSelector
    window.setActiveTab = tab => {
      if (competition?.instant_win_type === 'instant_win' || competition?.instantWinType === 'instant_win') {
        setActiveTab('overview');
        return;
      }

      setActiveTab(tab);
    };

    // Also attach to main component element for better access
    const mainElement = document.querySelector('[data-competition-detail]');
    if (mainElement) {
      mainElement._setActiveTab = setActiveTab;
    }

    window.addEventListener('selectNumbersClick', handleSelectNumbers);

    return () => {
      window.removeEventListener('selectNumbersClick', handleSelectNumbers);
      delete window.setActiveTab;
      const el = document.querySelector('[data-competition-detail]');
      if (el && el?._setActiveTab) {
        delete el?._setActiveTab;
      }
    };
  }, []);

  const handleNumberSelect = async number => {
    if (!user) {
      setError('Log in or create a free account to pick your own numbers, or choose Lucky Dip.');
      return;
    }

    const isUserSelected = userSelectedNumbers?.includes(number);
    const isUserReserved = userReservedNumbers?.some(res => res?.number === number);
    const isSelectedByOthers = selectedNumbers?.includes(number) && !isUserSelected;
    const isReservedByOthers = reservedNumbers?.some(res => res?.number === number && res?.userId !== user?.id);

    if (!isUserSelected && !isUserReserved && !isSelectedByOthers && !isReservedByOthers) {
      try {
        setError('');

        const settle = markInFlight([number]);
        const results = await competitionService
          ?.reserveMultipleNumbers(competition?.id, user?.id, [number])
          .finally(settle);
        const held = results?.successful?.[0];
        if (!held) {
          throw new Error(results?.errors?.[0]?.message || 'Failed to reserve number');
        }

        // The database's real hold time (10 minutes for picks).
        setUserReservedNumbers(prev =>
          mergeHolds(prev, [
            {
              number,
              reservationId: held?.reservationId,
              expiresAt: held?.expiresAt || new Date(Date.now() + 10 * 60 * 1000)?.toISOString(),
              type: 'selection'
            }
          ])
        );
      } catch (err) {
        const errorMessage = err?.message || 'Failed to reserve number';
        setError(errorMessage);

        setTimeout(() => {
          setError('');
        }, 5000);

        if (competition?.id) {
          loadNumberStates(competition?.id);
        }
      }
    }
  };

  // Enhanced multiple number selection handler
  const handleMultipleNumberSelect = async numbers => {
    if (!user) {
      setError('Log in or create a free account to pick your own numbers, or choose Lucky Dip.');
      return;
    }

    if (!numbers || numbers?.length === 0) {
      setError('No numbers selected');
      return;
    }

    try {
      setError('');

      const settle = markInFlight(numbers);
      const results = await competitionService
        ?.reserveMultipleNumbers(competition?.id, user?.id, numbers)
        .finally(settle);

      if (results?.successful?.length > 0) {
        // The database's real hold time (10 minutes for picks). Numbers you
        // already held come back with their existing time.
        const newReservations = results?.successful?.map(res => ({
          number: res?.number,
          reservationId: res?.reservationId,
          expiresAt: res?.expiresAt || new Date(Date.now() + 10 * 60 * 1000)?.toISOString()
        }));

        setUserReservedNumbers(prev => mergeHolds(prev, newReservations));
      }

      if (results?.errors?.length > 0) {
        const errorMessages = results?.errors?.map(err => `#${err?.number}: ${err?.message}`);
        setError(`Some numbers could not be reserved:\n${errorMessages?.join('\n')}`);

        setTimeout(() => {
          setError('');
        }, 8000);
      }
    } catch (err) {
      const errorMessage = err?.message || 'Failed to reserve numbers';
      setError(errorMessage);

      setTimeout(() => {
        setError('');
      }, 5000);

      if (competition?.id) {
        loadNumberStates(competition?.id);
      }
    }
  };

  // Enhanced deselect handler with multiple support
  const handleNumberDeselect = async number => {
    if (!user) return;

    try {
      setError('');

      const isReserved = userReservedNumbers?.some(res => res?.number === number);

      if (isReserved) {
        setUserReservedNumbers(prev => prev?.filter(res => res?.number !== number) || []);
        await competitionService?.releaseReservation(competition?.id, user?.id, number);
      } else {
        setUserSelectedNumbers(prev => prev?.filter(n => n !== number) || []);
        await competitionService?.removeNumber(competition?.id, user?.id, number);
      }
    } catch (err) {
      setError(err?.message || 'Failed to deselect number');

      setTimeout(() => {
        setError('');
      }, 3000);

      if (competition?.id) {
        loadNumberStates(competition?.id);
      }
    }
  };

  // Enhanced multiple deselect handler
  const handleMultipleNumberDeselect = async numbers => {
    if (!user || !numbers || numbers?.length === 0) return;

    try {
      setError('');

      const reservedToRelease = numbers?.filter(num => userReservedNumbers?.some(res => res?.number === num));
      const selectedToRemove = numbers?.filter(num => userSelectedNumbers?.includes(num));

      if (reservedToRelease?.length > 0) {
        setUserReservedNumbers(prev => prev?.filter(res => !reservedToRelease?.includes(res?.number)) || []);
      }
      if (selectedToRemove?.length > 0) {
        setUserSelectedNumbers(prev => prev?.filter(n => !selectedToRemove?.includes(n)) || []);
      }

      if (reservedToRelease?.length > 0) {
        await competitionService?.releaseMultipleReservations(competition?.id, user?.id, reservedToRelease);
      }

      for (const num of selectedToRemove) {
        await competitionService?.removeNumber(competition?.id, user?.id, num);
      }
    } catch (err) {
      setError(err?.message || 'Failed to deselect numbers');

      setTimeout(() => {
        setError('');
      }, 5000);

      if (competition?.id) {
        loadNumberStates(competition?.id);
      }
    }
  };

  const handleQuantityChange = newQuantity => {
    setQuantity(newQuantity);
  };

  const handlePromoCodeApply = (code, discount) => {
    console.log('Promo code applied:', code, discount);
  };

  const handleAgeVerificationChange = verified => {
    console.log('Age verification:', verified);
  };

  // Adding to the basket happens in TicketSelector, through the server.
  // Instant-win competitions work the same way: the numbers go into the
  // basket, and any instant win is awarded by the database once the entry
  // is paid for.

  // Instant-win competitions have no number picker: keep the prize tab open.
  // (Hooks must run before the early returns below.)
  useEffect(() => {
    if (competition?.instant_win_type === 'instant_win' || competition?.instantWinType === 'instant_win') {
      setActiveTab('overview');
    }
  }, [competition?.instant_win_type, competition?.instantWinType]);

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-background">
          <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-card rounded-xl border border-border p-6 animate-pulse">
                  <div className="h-64 bg-muted rounded-xl mb-4"></div>
                  <div className="h-8 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-card rounded-xl border border-border p-6 animate-pulse">
                  <div className="h-48 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error && !competition) {
    return (
      <>
        <div className="min-h-screen bg-background">
          <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-10">
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-destructive mb-2">Error Loading Competition</h2>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!competition) {
    return (
      <>
        <div className="min-h-screen bg-background">
          <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-10">
            <div className="bg-muted rounded-xl p-6">
              <h2 className="text-lg font-semibold text-muted-foreground">No Competition Found</h2>
              <p className="text-muted-foreground">The requested competition could not be found.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Combine all numbers for display (but track them separately for logic)
  const allSelectedNumbers = [...new Set([...(selectedNumbers || []), ...(userSelectedNumbers || [])])];

  // NEW: Calculate truly available numbers for lucky dip
  // (Sets: the sold list now holds every sold number, thousands on big draws.)
  const takenNumbers = new Set([
    ...(userPurchasedNumbers || []),
    ...(othersPurchasedNumbers || []),
    ...(userSelectedNumbers || []),
    ...(reservedNumbers || []).map(res => res?.number),
    ...(userReservedNumbers || []).map(res => res?.number)
  ]);
  const availableNumbers = [];
  for (let i = 1; i <= competition?.totalTickets; i++) {
    if (!takenNumbers.has(i)) {
      availableNumbers?.push(i);
    }
  }

  // Numbers can be picked while it's live and has opened (one published
  // ahead of time opens at its start date).
  const comingSoon = competition?.status === 'active' && !opened;
  const canSelectNumbers = competition?.status === 'active' && opened;
  // CRITICAL FIX: Hide number selection for instant win competitions
  const isInstantWin = competition?.instant_win_type === 'instant_win' || competition?.instantWinType === 'instant_win';
  const showNumberSelection = canSelectNumbers && !isInstantWin;
  const isDrawn = competition?.status === 'drawn';

  const competitionWinners = (pastWinners || []).filter(
    (w) => !w?.competitionId || w.competitionId === competition?.id
  );

  const tabClass = (on) =>
    `shrink-0 pb-3 border-b-[3px] font-semibold transition-colors ${
      on ? 'border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="min-h-screen bg-background">
      <CompetitionHero competition={competition} winners={competitionWinners} />
      <div className="bg-background" data-competition-detail>
        <div className="max-w-[1520px] mx-auto px-[var(--gutter)] pt-8 pb-16">
          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive/30 rounded-[8px] p-4">
              <div className="flex items-start space-x-2">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-destructive mt-0.5" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium mb-1">Reservation Error</p>
                  <p className="text-destructive text-sm whitespace-pre-wrap">{error}</p>
                </div>
                <button onClick={() => setError('')} className="text-destructive/60 hover:text-destructive transition-colors">
                  <Icon name="XMarkIcon" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Reservation Status Banner */}
          {userReservedNumbers?.length > 0 && (
            <div
              className={`fixed left-1/2 z-50 -translate-x-1/2 transition-all duration-300
      ${isReservationMinimised ? 'bottom-4 w-auto' : 'bottom-4 w-[calc(100%-2rem)] max-w-4xl'}`}
            >
              <div
                className={`bg-yellow-500/10 border border-yellow-500/20 rounded-xl shadow-xl backdrop-blur
        ${isReservationMinimised ? 'px-4 py-2 cursor-pointer' : 'p-4'}`}
                onClick={() => {
                  if (isReservationMinimised) {
                    setIsReservationMinimised(false);
                  }
                }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="ClockIcon" size={18} className="text-yellow-600" />
                    <div>
                      <p className="text-yellow-700 font-medium text-sm">{userReservedNumbers?.length} reserved</p>

                      {!isReservationMinimised && (
                        <p className="text-yellow-600 text-sm">
                          Picked numbers are held for 10 minutes, and for 15 once they&rsquo;re in your basket.
                        </p>
                      )}
                    </div>
                  </div>

                  {!isReservationMinimised && (
                    <div className="text-right">
                      <p className="text-yellow-700 text-sm font-medium">
                        Numbers:{' '}
                        {userReservedNumbers?.slice(0, 5)?.map(res => res?.number)?.join(', ')}
                        {userReservedNumbers?.length > 5 && ` +${userReservedNumbers?.length - 5} more`}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={e => {
                      e?.stopPropagation();
                      setIsReservationMinimised(prev => !prev);
                    }}
                    className="text-yellow-700 hover:text-yellow-900 transition-colors"
                    aria-label={isReservationMinimised ? 'Expand' : 'Minimise'}
                  >
                    <Icon name={isReservationMinimised ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-10">
            {/* Left column: the prize, number picker and rules */}
            <div className="lg:col-span-7 xl:col-span-8 min-w-0">
              <div id="pick-numbers" className="scroll-mt-28">
                <div role="tablist" aria-label="Competition" className="flex gap-7 overflow-x-auto scrollbar-none border-b border-white/[0.12]">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'overview'}
                    onClick={() => setActiveTab('overview')}
                    className={tabClass(activeTab === 'overview')}
                  >
                    The prize
                  </button>

                  {showNumberSelection && (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === 'numbers'}
                      onClick={() => setActiveTab('numbers')}
                      className={tabClass(activeTab === 'numbers')}
                    >
                      Pick your numbers
                      {(userSelectedNumbers?.length > 0 || userReservedNumbers?.length > 0) && (
                        <span className="ml-2 px-1.5 py-0.5 bg-accent text-accent-foreground text-xs font-black rounded-[4px] tabular-nums">
                          {(userSelectedNumbers?.length || 0) + (userReservedNumbers?.length || 0)}
                        </span>
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'entrants'}
                    onClick={() => setActiveTab('entrants')}
                    className={tabClass(activeTab === 'entrants')}
                  >
                    Entrants list
                  </button>

                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'rules'}
                    onClick={() => setActiveTab('rules')}
                    className={tabClass(activeTab === 'rules')}
                  >
                    Rules &amp; winners
                  </button>
                </div>

                <div className="pt-6">
                  {activeTab === 'overview' && (
                    <div className="space-y-8">
                      <PrizeGallery competition={competition} />

                      <CompetitionInfo competition={competition} instantWinPrizes={instantWinPrizes} />

                      <FreeEntryNotice competition={competition} />
                    </div>
                  )}

                  {activeTab === 'numbers' && showNumberSelection && (
                    <div>
                      <LuckyNumbers
                        totalTickets={competition?.totalTickets || 0}
                        takenNumbers={[
                          ...(othersPurchasedNumbers || []),
                          ...(selectedNumbers || []),
                          ...(reservedNumbers || [])
                            .filter(r => r?.userId !== user?.id)
                            .map(r => r?.number),
                        ]}
                        myNumbers={[
                          ...(userPurchasedNumbers || []),
                          ...(userSelectedNumbers || []),
                          ...(userReservedNumbers || []).map(r => r?.number),
                        ]}
                        onPick={handleMultipleNumberSelect}
                      />
                      <ManualNumberSelector
                        totalTickets={competition?.totalTickets}
                        selectedNumbers={selectedNumbers}
                        userSelectedNumbers={userSelectedNumbers}
                        userPurchasedNumbers={userPurchasedNumbers}
                        othersPurchasedNumbers={othersPurchasedNumbers}
                        reservedNumbers={reservedNumbers}
                        userReservedNumbers={userReservedNumbers}
                        onNumberSelect={handleNumberSelect}
                        onNumberDeselect={handleNumberDeselect}
                        onMultipleNumberSelect={handleMultipleNumberSelect}
                        onMultipleNumberDeselect={handleMultipleNumberDeselect}
                        ticketPrice={competition?.ticketPrice}
                        bulkPricingTiers={competition?.bulkPricingTiers || competition?.bulk_pricing_tiers || []}
                      />
                    </div>
                  )}

                  {activeTab === 'entrants' && <EntrantsList competition={competition} />}

                  {activeTab === 'rules' && (
                    <div>
                      <CompetitionRules competition={competition} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Ticket Selector */}
            <div id="tickets" className="lg:col-span-5 xl:col-span-4 scroll-mt-32">
              <div className="lg:sticky lg:top-32 space-y-6">
                {/* Live draw embed — renders nothing if no URL is set. */}
                <LiveDrawEmbed
                  url={competition?.liveStreamUrl}
                  title={`${competition?.title || 'Competition'} — Live Draw`}
                />

                {canSelectNumbers ? (
                  <>
                    <TicketSelector
                      competition={competition}
                      selectedNumbers={userSelectedNumbers}
                      reservedNumbers={userReservedNumbers}
                      availableNumbers={availableNumbers}
                      userPurchasedNumbers={userPurchasedNumbers}
                      isReservationMinimised={isReservationMinimised}
                      onQuantityChange={handleQuantityChange}
                      onPromoCodeApply={handlePromoCodeApply}
                      onAgeVerificationChange={handleAgeVerificationChange}
                    />

                    {!isInstantWin && (userSelectedNumbers?.length > 0 || userReservedNumbers?.length > 0) && (
                      <div className="bg-[rgba(50,216,0,0.08)] border border-[rgba(50,216,0,0.3)] rounded-[8px] p-4">
                        <h4 className="font-medium text-foreground mb-2">Your Selection</h4>
                        <div className="space-y-2 text-sm">
                          {userSelectedNumbers?.length > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Selected:</span>
                              <span className="font-medium text-accent">{userSelectedNumbers?.length} numbers</span>
                            </div>
                          )}
                          {userReservedNumbers?.length > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Reserved:</span>
                              <span className="font-medium text-yellow-600">{userReservedNumbers?.length} numbers</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : comingSoon ? (
                  <div className="rounded-[8px] border border-accent/40 bg-[#111612] p-6">
                    <p className="text-sm font-semibold text-accent-bright">Coming soon</p>
                    <p className="d mt-2 text-[length:var(--t-h3)]">Entries open {formatDraw(competition?.startDate)}.</p>
                    <p className="mt-3 text-muted-foreground">
                      Come back then to pick your numbers. This page opens for entries by itself at that time.
                    </p>
                    <a href="/all-competitions" className="btn-block btn-line mt-6">
                      See what&apos;s open now <Icon name="ArrowRightIcon" size={18} />
                    </a>
                  </div>
                ) : (
                  <div className="rounded-[8px] border border-white/10 bg-[#111612] p-6">
                    <p className="d text-[length:var(--t-h3)]">Fancy another go?</p>
                    <p className="mt-3 text-muted-foreground">
                      {isDrawn
                        ? 'This one has been drawn. Plenty more are open now.'
                        : 'Entries have closed on this one. Plenty more are open now.'}
                    </p>
                    <a href="/all-competitions" className="btn-block btn-go mt-6">
                      See what&apos;s open <Icon name="ArrowRightIcon" size={18} />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CompetitionDetailInteractive.propTypes = {
  initialData: PropTypes?.shape({
    competition: PropTypes?.object,
    pastWinners: PropTypes?.array
  })
};

export default CompetitionDetailInteractive;